'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';

import { useIsMobile } from '@web/hooks';

import { ChevronIcon, CloseIcon, PlusIcon, Tooltip } from '../../atomic-atoms';

import styles from './ContextTabs.module.scss';

export type ContextTabsDisplayMode = 'name' | 'section';
const CONTEXT_TAB_ACTION_WIDTH = 38;
// useIsMobile uses an exclusive comparison, so 1241 activates at <= 1240px.
const CONTEXT_TABS_COMPACT_BREAKPOINT = 1241;

export type ContextTabViewModel = {
  id: string;
  name: string;
  section?: string;
  fullLabel?: string;
  icon?: ReactNode;
  isDirty?: boolean;
};

export type ContextTabsProps = {
  tabs: ContextTabViewModel[];
  activeTabId: string;
  displayMode?: ContextTabsDisplayMode;
  canAdd?: boolean;
  addDisabledReason?: string;
  isCompact?: boolean;
  className?: string;
  'aria-label'?: string;
  onSelect: (tabId: string) => void;
  onClose: (tabId: string) => void;
  onAdd: () => void;
};

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
  );
}

function accessibleLabel(tab: ContextTabViewModel) {
  return tab.fullLabel ?? [tab.name, tab.section].filter(Boolean).join(' • ');
}

/**
 * Controlled, router-agnostic Context Tabs UI.
 *
 * Routing, persistence, dirty confirmation, and tab state transitions are
 * intentionally delegated to the consumer so this component can be reused by
 * any application in the monorepo.
 */
export function ContextTabs({
  tabs,
  activeTabId,
  displayMode = 'name',
  canAdd = true,
  addDisabledReason = 'Close a tab to open another',
  isCompact: compactOverride,
  className,
  'aria-label': ariaLabel = 'Context tabs',
  onSelect,
  onClose,
  onAdd,
}: ContextTabsProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());
  const [measuredCompact, setMeasuredCompact] = useState(false);
  const [allTabsOpen, setAllTabsOpen] = useState(false);
  const isNarrowViewport = useIsMobile(CONTEXT_TABS_COMPACT_BREAKPOINT);
  const isCompact = compactOverride ?? (isNarrowViewport || measuredCompact);

  useLayoutEffect(() => {
    if (compactOverride !== undefined) return;
    const root = rootRef.current;
    const tabList = tabListRef.current;
    if (!root || !tabList || typeof ResizeObserver === 'undefined') {
      return;
    }

    const measure = () => {
      root.dataset.measuringExpanded = 'true';
      const expandedWidth = tabList.scrollWidth;
      delete root.dataset.measuringExpanded;
      const availableWidth = Math.max(
        0,
        root.clientWidth - CONTEXT_TAB_ACTION_WIDTH,
      );
      setMeasuredCompact(expandedWidth > availableWidth + 1);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    measure();
    return () => observer.disconnect();
  }, [compactOverride, tabs]);

  useLayoutEffect(() => {
    tabRefs.current.get(activeTabId)?.scrollIntoView?.({
      block: 'nearest',
      inline: 'nearest',
    });
  }, [activeTabId, isCompact, tabs.length]);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if (
        !event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        isEditableTarget(event.target)
      ) {
        return;
      }

      const digit = Number(
        event.code.startsWith('Digit') ? event.code.slice(5) : event.key,
      );
      if (digit < 1 || digit > 8 || !tabs[digit - 1]) return;
      event.preventDefault();
      onSelect(tabs[digit - 1].id);
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [onSelect, tabs]);

  const moveFocus = useCallback(
    (currentIndex: number, direction: -1 | 1) => {
      if (!tabs.length) return;
      const nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
      tabRefs.current.get(tabs[nextIndex].id)?.focus();
    },
    [tabs],
  );

  const handleTabKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>, index: number) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveFocus(index, -1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveFocus(index, 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        tabRefs.current.get(tabs[0]?.id)?.focus();
      } else if (event.key === 'End') {
        event.preventDefault();
        tabRefs.current.get(tabs.at(-1)?.id ?? '')?.focus();
      } else if (event.key === 'Delete' && tabs.length > 1) {
        event.preventDefault();
        onClose(tabs[index].id);
      }
    },
    [moveFocus, onClose, tabs],
  );

  if (!tabs.length) return null;

  return (
    <div
      ref={rootRef}
      className={`${styles.root} ${className ?? ''}`}
      data-compact={isCompact || undefined}
    >
      <div className={styles.tabViewport}>
        <div
          ref={tabListRef}
          className={styles.tabList}
          role="tablist"
          aria-label={ariaLabel}
        >
          {tabs.map((tab, index) => {
            const active = tab.id === activeTabId;
            return (
              <div
                key={tab.id}
                className={styles.tabShell}
                data-active={active || undefined}
              >
                <Tooltip
                  title={accessibleLabel(tab)}
                  placement="bottom"
                  wrapperStyle={{
                    flex: '1 1 auto',
                    height: '100%',
                    minWidth: 0,
                  }}
                >
                  <button
                    ref={(element) => {
                      if (element) tabRefs.current.set(tab.id, element);
                      else tabRefs.current.delete(tab.id);
                    }}
                    type="button"
                    role="tab"
                    className={styles.tab}
                    aria-selected={active}
                    aria-label={accessibleLabel(tab)}
                    tabIndex={active ? 0 : -1}
                    onClick={() => onSelect(tab.id)}
                    onKeyDown={(event) => handleTabKeyDown(event, index)}
                  >
                    {tab.icon ? (
                      <span className={styles.icon} aria-hidden="true">
                        {tab.icon}
                      </span>
                    ) : null}
                    <span className={styles.labels}>
                      <span className={styles.name}>{tab.name}</span>
                      {displayMode === 'section' && tab.section ? (
                        <span className={styles.section}>{tab.section}</span>
                      ) : null}
                    </span>
                    {tab.isDirty ? (
                      <span className={styles.dirty} aria-hidden="true">
                        •
                      </span>
                    ) : null}
                    {tab.isDirty ? (
                      <span className={styles.srOnly} data-dirty-label>
                        Unsaved changes
                      </span>
                    ) : null}
                  </button>
                </Tooltip>

                {tabs.length > 1 ? (
                  <button
                    type="button"
                    className={styles.close}
                    aria-label={`Close ${tab.name}`}
                    onClick={() => onClose(tab.id)}
                  >
                    <CloseIcon size={16} styles={{ color: 'currentColor' }} />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <Tooltip
        title={canAdd ? 'New tab' : addDisabledReason}
        placement="bottom"
        wrapperStyle={{
          alignSelf: 'flex-end',
          flex: `0 0 ${CONTEXT_TAB_ACTION_WIDTH}px`,
          height: `${CONTEXT_TAB_ACTION_WIDTH}px`,
        }}
      >
        <button
          type="button"
          className={styles.action}
          aria-label="New tab"
          disabled={!canAdd}
          onClick={onAdd}
        >
          <PlusIcon size={14} styles={{ color: 'currentColor' }} />
        </button>
      </Tooltip>

      {isCompact ? (
        <div className={styles.allTabsWrap}>
          <Tooltip
            title="All tabs"
            placement="bottom"
            wrapperStyle={{
              height: `${CONTEXT_TAB_ACTION_WIDTH}px`,
              width: `${CONTEXT_TAB_ACTION_WIDTH}px`,
            }}
          >
            <button
              type="button"
              className={styles.action}
              aria-label="All tabs"
              aria-haspopup="menu"
              aria-expanded={allTabsOpen}
              onClick={() => setAllTabsOpen((open) => !open)}
            >
              <ChevronIcon
                direction="down"
                size={13}
                styles={{ color: 'currentColor' }}
              />
            </button>
          </Tooltip>
          {allTabsOpen ? (
            <div className={styles.menu} role="menu" aria-label="All tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="menuitem"
                  className={styles.menuItem}
                  data-active={tab.id === activeTabId || undefined}
                  onClick={() => {
                    onSelect(tab.id);
                    setAllTabsOpen(false);
                  }}
                >
                  {tab.icon ? (
                    <span className={styles.icon} aria-hidden="true">
                      {tab.icon}
                    </span>
                  ) : null}
                  <span>{accessibleLabel(tab)}</span>
                  {tab.isDirty ? (
                    <span aria-label="Unsaved changes">•</span>
                  ) : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
